import { useTranslation } from 'react-i18next'

import { languageName, setLanguage } from './index'
import { useOfferedLanguages } from './offeredLanguages'

/**
 * The interface language control.
 *
 * <para>
 * **Mounted twice: on the login screen and in the app bar** (`DECISIONS.md` #44). `I18N.md` §6
 * requires the login screen — a foreign medical team arriving in the wartime scenario must not
 * have to log in through a Swedish interface first — and that is a requirement about where the
 * control must be *reachable*, not a prohibition on the rest of the interface. §6 read "not
 * inside the system" and gave no reason for that half; the reason against it is a shared ward
 * terminal, where changing language meant signing out and sign-out discards the unsaved note or
 * correction that the discard guards exist to protect. See `LoginScreen` and `App`.
 * </para>
 *
 * <para>
 * It is one component in two places rather than two controls, so there is one behaviour to reason
 * about and `languages.length &lt; 2` is honoured identically in both. Positioning belongs to the
 * container — `.login__language` and the app bar's own flex row — and the control itself carries no
 * placement (`.language-choice` in index.css).
 * </para>
 *
 * <para>
 * **Nothing is hard-coded to two languages.** It enumerates the discovered catalogues and
 * labels each with that catalogue's own `language.name`, so a third language appears here by
 * existing as a directory under `src/locales` — §6 and `catalogues.ts` §1.
 * </para>
 *
 * <para>
 * **What it deliberately does not do is persist the choice.** §6 wants a per-user and a
 * per-device preference, and both are phase 1 — but the half that fails quietly is the
 * *restore*, not the write: a stored language whose catalogue has since been removed makes
 * `setLanguage()` throw, and an unhandled rejection while restoring a preference is a blank
 * screen during an outage. So this control changes the language for the session and stores
 * nothing, and a reload is back to Swedish. Whoever adds persistence validates against the
 * exported `languages` first and falls back to `REFERENCE_LANGUAGE`.
 * </para>
 *
 * <para>
 * Language choice **never** affects how a date, a dose or a unit is rendered (§3–4, AGENTS.md
 * §5). Those go through the shared formatters, which do not read `i18next.language` at all —
 * and that matters more as languages are added, not less: a "properly localised" interface is
 * exactly where `0.5 mg` would quietly become `0,5 mg`.
 * </para>
 */
export function LanguageChoice() {
  const { t, i18n } = useTranslation()

  // What this box offers (DECISIONS.md #56), not every catalogue the bundle carries.
  const languages = useOfferedLanguages()

  // One language means no choice to offer, and an empty select is worse than nothing.
  // Not a hypothetical: it is what a build with a single catalogue directory looks like.
  if (languages.length < 2) {
    return null
  }

  return (
    // Named after the control rather than after where it sits. It was `app-bar__language`
    // until it moved, and a class naming a parent it no longer has is the sort of thing that
    // gets styled twice — see `.language-choice` and `.login__language` in index.css.
    <label className="language-choice">
      {/*
        A real label rather than `aria-label`, so the control is hit-target-adjacent to
        its own word and readable by someone who is not using a screen reader either.
      */}
      <span className="language-choice__text">{t('language.choose')}</span>
      <select
        value={i18n.language}
        onChange={(event) => {
          // `setLanguage()` throws on a language with no catalogue, and from here that is
          // unreachable — the options *are* the discovered catalogues. So a rejection is a
          // programming error and §6 asks for it to be loud rather than swallowed; there is
          // deliberately no `.catch()` turning it into a silent no-op that leaves the
          // interface in one language and the control showing another.
          void setLanguage(event.target.value)
        }}
      >
        {languages.map((language) => (
          // `lang` on the option, because the option's text is in the language it names —
          // "Svenska" inside an English document is still Swedish, and a screen reader
          // reading it with English phonemes is how that goes wrong.
          <option key={language} value={language} lang={language}>
            {languageName(language)}
          </option>
        ))}
      </select>
    </label>
  )
}
